import React, { useEffect, useState } from "react";
import UIkit from "uikit";
import { Image } from "uikit-react";
import { Link } from "react-router-dom";
import { usePermissionsStore } from "./permissions_store";

const isTemporaryFallbackRole = (role) =>
  String(role?.name || "").trim().toLowerCase() === "no assigned role";

export default function RolesPage() {
  const { roles, rolesLoaded, loadRoles, createRole, deleteRole, canEdit } = usePermissionsStore();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!rolesLoaded) loadRoles();
  }, [rolesLoaded, loadRoles]);

  useEffect(() => {
    const modal = UIkit.modal("#modal-role-delete");

    if (roleToDelete) {
      modal.show();
      return;
    }

    modal.hide();
  }, [roleToDelete]);

  const handleAddRole = () => {
    setNewName("");
    setNewDesc("");
    setCreating(true);
  };

  const handleCancelCreate = () => {
    setNewName("");
    setNewDesc("");
    setCreating(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      UIkit.notification("Role name is required", { status: "warning" });
      return;
    }
    setSaving(true);
    try {
      await createRole({ name: newName.trim(), description: newDesc.trim() });
      setNewName("");
      setNewDesc("");
      setCreating(false);
      UIkit.offcanvas("#modal-role-add").hide();
      UIkit.notification("Role created", { status: "success" });
    } catch (err) {
      UIkit.notification("Failed to create role", { status: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
  };

  const handleCancelDelete = () => {
    if (deleting) {
      return;
    }
    setRoleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) {
      return;
    }

    setDeleting(true);

    try {
      await deleteRole(roleToDelete.id);
      UIkit.notification("Role deleted", { status: "success" });
      setRoleToDelete(null);
    } catch {
      UIkit.notification("Failed to delete role", { status: "danger" });
    } finally {
      setDeleting(false);
    }
  };

  const canManage = canEdit("admin.roles");
  const visibleRoles = roles.filter((role) => !isTemporaryFallbackRole(role));

  return (
    <section id="roles-page">
      <div id="hms-page-title">
        <div className="uk-flex uk-flex-between uk-flex-middle">
          <h1 className="uk-heading-small">Roles &amp; Permissions</h1>
        </div>
      </div>

      <div id="hms-main-body">
        {canManage && (
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #modal-role-add"
                onClick={handleAddRole}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Role
              </button>
            </div>
          </div>
        )}
        <div className="secondary uk-margin-small-bottom">
          <strong>System roles</strong> are built-in and cannot be deleted or modified. Create <strong>custom roles</strong> for organization-specific access patterns. <br/>Each user has <strong>one role</strong>. The account owner always has full access. Only the owner can appoint administrators or remove users.
        </div>

        <div className="uk-card uk-card-default">
          <div className="hms-simplebar" data-simplebar>
            <table className="uk-table uk-table-divider hms-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th className="uk-text-center">Users</th>
                  <th className="uk-text-center">Type</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {!rolesLoaded && (
                  <tr>
                    <td colSpan={5} className="uk-text-center secondary">Loading…</td>
                  </tr>
                )}
                {rolesLoaded && visibleRoles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="uk-text-center secondary">No roles found</td>
                  </tr>
                )}
                {visibleRoles.map((role) => (
                  <tr key={role.id}>
                    <td><strong>{role.name}</strong></td>
                    <td className="secondary">{role.description}</td>
                    <td className="uk-text-center">{role.user_count ?? 0}</td>
                    <td className="uk-text-center">
                      {role.is_system_role ? (
                        <span className="hms-booking-tag">System</span>
                      ) : (
                        <span className="hms-chip priority-medium">Custom</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="hms-action-btn">
                        {!role.is_system_role ? (
                          <div className="uk-flex uk-flex-top">
                            <div className="uk-inline">
                              <Link to={`/roles/${role.id}`} className="uk-button" uk-tooltip="Edit">
                                <Image src="/images/edit.svg" />
                              </Link>
                            </div>
                            <div className="uk-inline">
                              <button className="uk-button" onClick={() => handleDelete(role)} uk-tooltip="Delete">
                                <Image src="/images/delete.svg" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <Link to={`/roles/${role.id}`} className="uk-button" uk-tooltip="View">
                            <Image src="/images/view.svg" />
                          </Link>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        id="modal-role-add"
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              uk-close="true"
              onClick={handleCancelCreate}
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">Add Role</h2>
            </div>
            <div className="uk-modal-body">
              <form onSubmit={handleCreate}>
                <div className="uk-grid-small" uk-grid="true">
                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Role Name *</label>
                    <input
                      className="uk-input"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="e.g. Regional Manager"
                      autoFocus={creating}
                    />
                  </div>
                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Description</label>
                    <input
                      className="uk-input"
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      placeholder="Optional description"
                    />
                  </div>
                </div>
                <div className="uk-margin-top">
                  <button
                    type="submit"
                    className="uk-button hms-btn hms-red-btn"
                    disabled={saving}
                  >
                    {saving ? <div data-uk-spinner="ratio: 0.5;"></div> : "Save"}
                  </button>
                  <button
                    type="button"
                    className="uk-button hms-btn uk-offcanvas-close"
                    onClick={handleCancelCreate}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div
        id="modal-role-delete"
        className="uk-modal"
        uk-modal="true"
      >
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={handleCancelDelete}
            disabled={deleting}
          ></button>
          <h2 className="uk-modal-title">Delete Role</h2>
          <p>
            Delete role "{roleToDelete?.name}"? This cannot be undone.
          </p>
          <div className="uk-flex uk-flex-right uk-margin-top">
            <button
              type="button"
              className="uk-button uk-button-default uk-modal-close"
              onClick={handleCancelDelete}
              disabled={deleting}
              autoFocus={!!roleToDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="uk-button uk-button-danger uk-margin-small-left"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
